/* Simulador de cálculo: qualquer categoria, pelo motor de cálculo da ZNIT, sem gravar nada.
   Os resultados da sessão ficam numa tabela para comparar (e exportar), só no navegador. */

import { api } from '../core/api';
import { baixarCSV } from '../core/arquivos';
import { CFG } from '../core/config';
import { esc, isoDe, nf } from '../core/formato';
import type { Campo, ItemLista, SimCatalogo, SimCategoria, SimResultado } from '../tipos/api';
import { erroBox, escLabel, loading } from '../ui/componentes';
import { $, ic, setHtml } from '../ui/dom';
import { toast } from '../ui/toast';
import { render } from '../roteador';
import type { Pagina } from './pagina';

interface Historico extends SimResultado {
  n: number;
  resumo: string;
}

const sim = {
  /** Modo, sessão e empresa a que o estado pertence: mudou, recomeça (ex.: saiu e entrou na demonstração). */
  dono: '',
  catalogo: null as SimCatalogo | null,
  slug: '',
  versao: '',
  valores: {} as Record<string, string>,
  listas: new Map<string, ItemLista[]>(),
  resultado: null as SimResultado | null,
  erro: '',
  historico: [] as Historico[],
  contador: 0,
};

const chaveLista = (nome: string) => `${sim.versao}|${nome}`;

function conferirDono(): void {
  const dono = `${CFG.modo}|${CFG.base}|${CFG.token || CFG.apiKey}|${CFG.cnpj}`;
  if (dono === sim.dono) return;
  Object.assign(sim, { dono, catalogo: null, slug: '', versao: '', valores: {}, listas: new Map(), resultado: null, erro: '', historico: [], contador: 0 });
}
const categoria = (): SimCategoria | undefined => sim.catalogo?.categorias.find((c) => c.slug === sim.slug);
const temPadrao = (c: Campo) => c.padrao !== null && c.padrao !== undefined;

async function carregarListas(c: SimCategoria): Promise<void> {
  const faltam = c.listas.filter((n) => !sim.listas.has(chaveLista(n)));
  if (!faltam.length) return;
  const r = await api<Record<string, ItemLista[]>>('GET', '/simulacao/listas', { params: { nomes: faltam.join(','), versao_ghg: sim.versao } });
  Object.entries(r).forEach(([n, itens]) => sim.listas.set(chaveLista(n), itens));
}

function campoHtml(c: Campo): string {
  const id = `s_${c.nome}`;
  const v = sim.valores[c.nome] ?? (c.nome === 'data' ? isoDe(new Date()) : temPadrao(c) ? String(c.padrao) : '');
  let input: string;
  if (c.tipo === 'opcoes') {
    input = `<select class="inp" id="${id}">${!c.obrigatorio && !temPadrao(c) ? '<option value="">—</option>' : ''}${c.opcoes.map((o) =>
      `<option ${o === v ? 'selected' : ''}>${esc(o)}</option>`).join('')}</select>`;
  } else if (c.tipo.startsWith('lista:')) {
    const itens = sim.listas.get(chaveLista(c.tipo.slice(6))) || [];
    input = `<select class="inp" id="${id}"><option value="">${c.obrigatorio ? 'Selecione…' : '—'}</option>${itens.map((i) =>
      `<option value="${esc(i.id)}" ${String(i.id) === v ? 'selected' : ''}>${esc(i.nome)}${i.unidade ? ` (${esc(i.unidade)})` : ''}</option>`).join('')}</select>`;
  } else {
    input = `<input class="inp" id="${id}" ${c.tipo === 'data' ? 'type="date"' : ''} ${c.tipo === 'numero' ? 'inputmode="decimal"' : ''} value="${esc(v)}">`;
  }
  return `<div class="field"><label for="${id}">${esc(c.rotulo)}${c.obrigatorio && !temPadrao(c) ? ' <span class="req">*</span>' : ''}</label>${input}
    ${c.dica ? `<div class="hint">${esc(c.dica)}</div>` : ''}</div>`;
}

function lerFormulario(c: SimCategoria): void {
  c.campos.forEach((x) => {
    const el = document.getElementById(`s_${x.nome}`) as HTMLInputElement | null;
    if (el) sim.valores[x.nome] = el.value.trim();
  });
}

function opcoesCategorias(): string {
  return [1, 2, 3].map((e) => {
    const cs = (sim.catalogo?.categorias || []).filter((c) => c.escopo === e);
    return cs.length ? `<optgroup label="${escLabel(e)}">${cs.map((c) =>
      `<option value="${c.slug}" ${c.slug === sim.slug ? 'selected' : ''}>${esc(c.nome)}</option>`).join('')}</optgroup>` : '';
  }).join('');
}

const t = (v: number, casas = 4) => nf(v, Math.abs(v) >= 100 ? 2 : casas);

function resultadoHtml(): string {
  if (sim.erro) return `<div class="callout warn">${ic('i-alert')}<div><b>Não foi possível calcular.</b><br>${esc(sim.erro)}</div></div>`;
  const r = sim.resultado;
  if (!r) {
    return `<div class="empty"><div class="icn">${ic('i-calc')}</div><h3>Preencha e clique em Calcular</h3>
      <p>O resultado aparece aqui. Nada é gravado no inventário.</p></div>`;
  }
  const e = r.emissoes;
  const linhas = [
    ['CO₂', e.emissao_co2, e.emissao_co2, 1],
    ['CH₄', e.emissao_ch4, e.emissao_ch4 * 28, 28],
    ['N₂O', e.emissao_n2o, e.emissao_n2o * 265, 265],
  ] as const;
  const detalhes = Object.entries(r.detalhes).filter(([, v]) => v !== null && v !== '' && typeof v !== 'object');
  return `<div class="card">
    <div class="between"><h2 class="sec" style="margin:0">${esc(r.categoria.nome)}</h2><span class="pill grey">${escLabel(r.categoria.escopo)} · ${esc(r.versao_ghg)}</span></div>
    <div class="kpi-val" style="margin:18px 0 6px"><b class="num">${t(e.emissao_total)}</b><span>tCO₂e</span></div>
    <p style="margin:0 0 16px;color:var(--muted);font-size:13px">Referência ${esc(r.data.split('-').reverse().join('/'))} · simulação, não gravada</p>
    <div class="table-wrap"><table class="table" style="font-size:13px">
      <thead><tr><th>Gás</th><th class="r">Emissão (t)</th><th class="r">GWP</th><th class="r">tCO₂e</th></tr></thead>
      <tbody>${linhas.map(([g, tonel, co2e, gwp]) => `<tr><td>${g}</td><td class="r num">${t(tonel, 6)}</td><td class="r num">${gwp}</td><td class="r num">${t(co2e)}</td></tr>`).join('')}</tbody>
      <tfoot><tr><td>Total</td><td></td><td></td><td class="r num">${t(e.emissao_total)}</td></tr></tfoot></table></div>
    ${e.emissao_biogenica ? `<p style="margin:12px 0 0;font-size:13px">CO₂ biogênico (reportado à parte): <b class="num">${t(e.emissao_biogenica)} t</b></p>` : ''}
    ${detalhes.length ? `<details class="adv" style="margin-top:16px"><summary>Detalhes do cálculo (fatores e consumos)</summary>
      <div class="table-wrap"><table class="table" style="font-size:12.5px"><tbody>${detalhes.map(([k, v]) =>
        `<tr><td>${esc(k.replace(/_/g, ' '))}</td><td class="r num">${esc(typeof v === 'number' ? nf(v, Math.abs(v) < 1 ? 6 : 3) : String(v))}</td></tr>`).join('')}</tbody></table></div></details>` : ''}
  </div>`;
}

function historicoHtml(): string {
  if (!sim.historico.length) return '';
  const total = sim.historico.reduce((s, h) => s + h.emissoes.emissao_total, 0);
  return `<div class="card" style="margin-top:18px">
    <div class="between" style="margin-bottom:12px"><h2 class="sec" style="margin:0">Simulações desta sessão</h2>
      <div class="row" style="gap:8px"><button class="btn btn-ghost" data-clique="exportar">${ic('i-dl')} CSV</button>
      <button class="btn btn-plain" data-clique="limpar">Limpar</button></div></div>
    <div class="table-wrap"><table class="table" style="font-size:13px">
      <thead><tr><th>#</th><th>Categoria</th><th>Entradas</th><th class="r">tCO₂e</th><th></th></tr></thead>
      <tbody>${sim.historico.map((h) => `<tr><td class="num">${h.n}</td><td><b>${esc(h.categoria.nome)}</b><br><small style="color:var(--muted)">${escLabel(h.categoria.escopo)} · ${esc(h.versao_ghg)}</small></td>
        <td style="font-size:12px;color:var(--ink-2)">${esc(h.resumo)}</td><td class="r num"><b>${t(h.emissoes.emissao_total)}</b></td>
        <td class="r"><button class="btn-plain" data-clique="remover" data-n="${h.n}" title="Remover">${ic('i-x', 'width:14px;height:14px')}</button></td></tr>`).join('')}</tbody>
      <tfoot><tr><td></td><td>Soma</td><td></td><td class="r num">${t(total)}</td><td></td></tr></tfoot></table></div>
    <p style="margin:10px 0 0;font-size:12px;color:var(--muted)">Fica só neste navegador até você recarregar a página.</p></div>`;
}

/** Entradas legíveis (nomes das listas no lugar dos IDs). */
function resumoEntradas(c: SimCategoria): string {
  return c.campos.filter((x) => x.nome !== 'data' && sim.valores[x.nome]).map((x) => {
    let v = sim.valores[x.nome];
    if (x.tipo.startsWith('lista:')) v = sim.listas.get(chaveLista(x.tipo.slice(6)))?.find((i) => String(i.id) === v)?.nome || v;
    return `${x.rotulo.replace(/\s*\(.*\)$/, '')}: ${v}`;
  }).slice(0, 4).join(' · ');
}

function formularioHtml(): string {
  const c = categoria();
  if (!c) return '';
  return `<div class="field"><label for="simVersao">Versão do GHG Protocol</label>
      <select class="inp" id="simVersao" data-muda="versao">${(sim.catalogo?.versoes || []).map((v) => `<option ${v === sim.versao ? 'selected' : ''}>${esc(v)}</option>`).join('')}</select></div>
    <p style="margin:0 0 14px;color:var(--muted);font-size:13px">${esc(c.descricao)}</p>
    ${c.aviso ? `<div class="callout warn" style="margin-bottom:16px">${ic('i-info')}<div>${esc(c.aviso)}</div></div>` : ''}
    <div id="simCampos">${c.listas.every((n) => sim.listas.has(chaveLista(n))) ? `<div class="grid g2" style="gap:0 14px">${c.campos.map(campoHtml).join('')}</div>` : loading('Carregando listas…')}</div>
    <button class="btn btn-primary" data-clique="calcular">${ic('i-calc')} Calcular</button>`;
}

export const simulador: Pagina = {
  semCatalogo: true,

  render: () => {
    conferirDono();
    return `
  <h1 class="page">Simulador de cálculo</h1>
  <p class="sub">Calcule as emissões de qualquer categoria com o motor da ZNIT, sem gravar nada no inventário. Use para conferir um fator, comparar alternativas ou estimar antes de lançar.</p>
  <div class="grid g2" style="align-items:start;grid-template-columns:minmax(0,1.1fr) minmax(0,1fr)">
    <div class="card">
      <h2 class="sec">Categoria</h2>
      <div class="field"><select class="inp" id="simCategoria" data-muda="categoria">${sim.catalogo ? opcoesCategorias() : '<option>Carregando…</option>'}</select></div>
      <div id="simForm">${sim.catalogo ? formularioHtml() : loading('Carregando categorias…')}</div>
    </div>
    <div><div id="simResultado">${resultadoHtml()}</div><div id="simHistorico">${historicoHtml()}</div></div>
  </div>`;
  },

  async carregar(tk) {
    try {
      if (!sim.catalogo) {
        sim.catalogo = await api<SimCatalogo>('GET', '/simulacao/catalogo');
        sim.versao = sim.catalogo.versao_padrao;
        sim.slug = sim.slug || sim.catalogo.categorias[0]?.slug || '';
      }
      const c = categoria();
      if (c && !c.listas.every((n) => sim.listas.has(chaveLista(n)))) {
        await carregarListas(c);
        render();
        return;
      }
      if (document.getElementById('simCategoria')?.textContent === 'Carregando…') render();
    } catch (e) {
      setHtml('simForm', erroBox(e));
    }
    void tk;
  },

  acoes: {
    categoria(el) {
      const c = categoria();
      if (c) lerFormulario(c);
      sim.slug = (el as HTMLSelectElement).value;
      sim.resultado = null;
      sim.erro = '';
      render();
    },

    versao(el) {
      const c = categoria();
      if (c) lerFormulario(c);
      sim.versao = (el as HTMLSelectElement).value;
      render();
    },

    async calcular(el) {
      const c = categoria();
      if (!c) return;
      lerFormulario(c);
      el.classList.add('dis');
      try {
        const valores = Object.fromEntries(Object.entries(sim.valores).filter(([k, v]) => v !== '' && c.campos.some((x) => x.nome === k)));
        sim.resultado = await api<SimResultado>('POST', '/simulacao', { body: { categoria: c.slug, versao_ghg: sim.versao, ...valores } });
        sim.erro = '';
        sim.historico.unshift({ ...sim.resultado, n: ++sim.contador, resumo: resumoEntradas(c) });
      } catch (e) {
        sim.erro = (e as Error).message;
        sim.resultado = null;
      }
      el.classList.remove('dis');
      setHtml('simResultado', resultadoHtml());
      setHtml('simHistorico', historicoHtml());
      if (window.innerWidth < 1100) $('#simResultado').scrollIntoView({ behavior: 'smooth' });
    },

    remover(el) {
      sim.historico = sim.historico.filter((h) => String(h.n) !== el.dataset.n);
      setHtml('simHistorico', historicoHtml());
    },

    limpar() {
      sim.historico = [];
      setHtml('simHistorico', historicoHtml());
    },

    exportar() {
      baixarCSV(`simulacoes_${isoDe(new Date())}.csv`, [
        ['n', 'categoria', 'escopo', 'versao_ghg', 'data_referencia', 'entradas', 'co2_t', 'ch4_t', 'n2o_t', 'total_tco2e', 'co2_biogenico_t'],
        ...[...sim.historico].reverse().map((h) => [h.n, h.categoria.nome, h.categoria.escopo, h.versao_ghg, h.data, h.resumo,
          h.emissoes.emissao_co2, h.emissoes.emissao_ch4, h.emissoes.emissao_n2o, h.emissoes.emissao_total, h.emissoes.emissao_biogenica]),
      ]);
      toast('CSV das simulações baixado.');
    },
  },
};
